/**
 * JsonInspector - Collapsible, syntax-highlighted JSON viewer.
 *
 * Displays JSON data with syntax highlighting, expand/collapse nodes,
 * copy functionality, and search-within-tree. Implements the spec from
 * STREAM_SHARED_COMPONENTS.md "Detailed API Specifications".
 *
 * @example Basic usage
 * ```tsx
 * <JsonInspector data={eventPayload} />
 * ```
 *
 * @example With all features
 * ```tsx
 * <JsonInspector
 *   data={complexObject}
 *   defaultExpand="first-level"
 *   showCopy
 *   showSearch
 *   showLineNumbers
 * />
 * ```
 */

import {
  useState,
  useCallback,
  useMemo,
  type CSSProperties,
  type ReactNode,
  type KeyboardEvent,
} from 'react'
import { ChevronRight, ChevronDown, Copy, Search, Check } from 'lucide-react'
import { analyzeBase64, type Base64Analysis } from '@/lib/base64'

// ── Types ────────────────────────────────────────────────────────────────────

export type JsonDensity = 'compact' | 'comfortable'
export type JsonDefaultExpand = 'all' | 'first-level' | 'collapsed'

export interface JsonInspectorProps {
  /** The data to display */
  data: unknown

  // ── Variants ───────────────────────────────────────────────────────────────

  /** Density mode. Default 'compact' */
  density?: JsonDensity
  /** Default expansion state. Default 'first-level' */
  defaultExpand?: JsonDefaultExpand
  /** Show copy button. Default true */
  showCopy?: boolean
  /** Show search input. Default true */
  showSearch?: boolean
  /** Show line numbers. Default false */
  showLineNumbers?: boolean
  /**
   * Auto-detect base64-encoded string values and offer an inline decoded view.
   * Default true. When true, strings that look like base64 and decode to
   * printable text get a "base64" badge plus an expanded view of their
   * decoded content (parsed as JSON if applicable).
   */
  autoDetectBase64?: boolean

  // ── Slots ──────────────────────────────────────────────────────────────────

  /** Custom value renderer. Return undefined to use default */
  renderValue?: (value: unknown, path: string[]) => ReactNode | undefined
  /** Trailing header content (e.g., "Open in new tab" link) */
  trailingHeader?: ReactNode
}

// ── Helpers ──────────────────────────────────────────────────────────────────

function getType(value: unknown): string {
  if (value === null) return 'null'
  if (Array.isArray(value)) return 'array'
  return typeof value
}

function isExpandable(value: unknown): boolean {
  return (typeof value === 'object' && value !== null)
}

// Maximum nesting depth to prevent stack overflow with deeply nested structures
const MAX_DEPTH = 50

// Field keys whose values are opaque identifiers that must never be treated as
// base64, even when they structurally resemble it. Decoding an `insert_id` or
// trace ID produces misleading garbage in the "Decoded" panel. The hex-only
// guard in analyzeBase64 catches most of these, but this denylist also covers
// identifiers that happen to use the full base64 alphabet (mixed case, +/=).
// Matched case-insensitively against the field key.
const BASE64_SKIP_KEYS = new Set([
  'insert_id',
  'insertid',
  'trace_id',
  'traceid',
  'trace',
  'span_id',
  'spanid',
  'log_name',
  'logname',
  'id',
])

function getInitialExpanded(
  data: unknown,
  defaultExpand: JsonDefaultExpand,
  path: string[] = []
): Set<string> {
  const expanded = new Set<string>()

  if (defaultExpand === 'collapsed') return expanded

  function traverse(value: unknown, currentPath: string[]) {
    const pathKey = currentPath.join('.')

    if (isExpandable(value)) {
      if (defaultExpand === 'all' || (defaultExpand === 'first-level' && currentPath.length === 0)) {
        expanded.add(pathKey)
      }

      if (defaultExpand === 'all') {
        if (Array.isArray(value)) {
          value.forEach((item, index) => {
            traverse(item, [...currentPath, String(index)])
          })
        } else if (typeof value === 'object' && value !== null) {
          Object.entries(value).forEach(([key, val]) => {
            traverse(val, [...currentPath, key])
          })
        }
      }
    }
  }

  traverse(data, path)
  return expanded
}

// ── Node Component ───────────────────────────────────────────────────────────

interface JsonNodeProps {
  keyName: string | null
  value: unknown
  path: string[]
  depth: number
  expanded: Set<string>
  onToggle: (path: string) => void
  selectedPath: string | null
  onSelect: (path: string) => void
  searchTerm: string
  showLineNumbers: boolean
  lineNumber: { current: number }
  renderValue?: (value: unknown, path: string[]) => ReactNode | undefined
  density: JsonDensity
  autoDetectBase64: boolean
}

function JsonNode({
  keyName,
  value,
  path,
  depth,
  expanded,
  onToggle,
  selectedPath,
  onSelect,
  searchTerm,
  showLineNumbers,
  lineNumber,
  renderValue,
  density,
  autoDetectBase64,
}: JsonNodeProps): ReactNode {
  // Detect base64-encoded string values and render them with an inline
  // decoded view. We do this before the normal scalar render path because
  // base64 strings have their own expand/collapse affordance.
  const skipBase64 = keyName != null && BASE64_SKIP_KEYS.has(keyName.toLowerCase())
  if (autoDetectBase64 && !skipBase64 && typeof value === 'string') {
    const analysis = analyzeBase64(value)
    if (analysis.isBase64) {
      return (
        <Base64StringNode
          keyName={keyName}
          value={value}
          analysis={analysis}
          path={path}
          depth={depth}
          expanded={expanded}
          onToggle={onToggle}
          selectedPath={selectedPath}
          onSelect={onSelect}
          searchTerm={searchTerm}
          showLineNumbers={showLineNumbers}
          lineNumber={lineNumber}
          renderValue={renderValue}
          density={density}
          autoDetectBase64={autoDetectBase64}
        />
      )
    }
  }

  const pathKey = path.join('.')
  const isExpanded = expanded.has(pathKey)
  const isSelected = selectedPath === pathKey
  const canExpand = isExpandable(value)
  const type = getType(value)
  const currentLine = lineNumber.current++

  // Prevent infinite recursion for deeply nested structures
  if (depth > MAX_DEPTH) {
    return (
      <div style={{ paddingLeft: `${depth * 16}px`, color: 'var(--text-muted)', fontStyle: 'italic' }}>
        ... (max depth reached)
      </div>
    )
  }

  const matchesSearch = searchTerm && (
    (keyName && keyName.toLowerCase().includes(searchTerm.toLowerCase())) ||
    (typeof value === 'string' && value.toLowerCase().includes(searchTerm.toLowerCase())) ||
    (typeof value === 'number' && String(value).includes(searchTerm))
  )

  // Custom render for value
  const customRendered = renderValue?.(value, path)

  const handleClick = () => {
    onSelect(pathKey)
    if (canExpand) {
      onToggle(pathKey)
    }
  }

  const handleKeyDown = (e: KeyboardEvent) => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault()
      handleClick()
    } else if (e.key === 'ArrowRight' && canExpand && !isExpanded) {
      e.preventDefault()
      onToggle(pathKey)
    } else if (e.key === 'ArrowLeft' && canExpand && isExpanded) {
      e.preventDefault()
      onToggle(pathKey)
    }
  }

  const indent = depth * 16
  const rowPadding = density === 'compact' ? '2px 0' : '4px 0'

  const rowStyle: CSSProperties = {
    ...styles.row,
    padding: rowPadding,
    paddingLeft: `${indent}px`,
    background: isSelected ? 'var(--bg-hover)' : matchesSearch ? 'rgba(59, 130, 246, 0.1)' : undefined,
  }

  // Render scalar values
  if (!canExpand) {
    return (
      <div
        style={rowStyle}
        onClick={handleClick}
        onKeyDown={handleKeyDown}
        tabIndex={0}
        role="treeitem"
        aria-selected={isSelected}
      >
        {showLineNumbers && <span style={styles.lineNumber}>{currentLine}</span>}
        {canExpand && <span style={styles.expandIcon} />}
        {keyName !== null && (
          <>
            <span style={styles.key}>{keyName}</span>
            <span style={styles.colon}>: </span>
          </>
        )}
        {customRendered !== undefined ? (
          customRendered
        ) : (
          <span style={getValueStyle(type)}>{formatValue(value)}</span>
        )}
      </div>
    )
  }

  // Render object/array
  const isArray = Array.isArray(value)
  const entries = isArray
    ? (value as unknown[]).map((v, i) => [String(i), v] as const)
    : Object.entries(value as Record<string, unknown>)
  const bracketOpen = isArray ? '[' : '{'
  const bracketClose = isArray ? ']' : '}'
  const summary = isArray ? `Array(${entries.length})` : `Object(${entries.length})`

  return (
    <div role="treeitem" aria-expanded={isExpanded} aria-selected={isSelected}>
      <div
        style={rowStyle}
        onClick={handleClick}
        onKeyDown={handleKeyDown}
        tabIndex={0}
      >
        {showLineNumbers && <span style={styles.lineNumber}>{currentLine}</span>}
        <span style={styles.expandIcon}>
          {isExpanded ? <ChevronDown size={12} /> : <ChevronRight size={12} />}
        </span>
        {keyName !== null && (
          <>
            <span style={styles.key}>{keyName}</span>
            <span style={styles.colon}>: </span>
          </>
        )}
        <span style={styles.bracket}>{bracketOpen}</span>
        {!isExpanded && (
          <>
            <span style={styles.summary}>{summary}</span>
            <span style={styles.bracket}>{bracketClose}</span>
          </>
        )}
      </div>

      {isExpanded && (
        <div role="group">
          {entries.map(([key, val]) => (
            <JsonNode
              key={key}
              keyName={isArray ? null : key}
              value={val}
              path={[...path, key]}
              depth={depth + 1}
              expanded={expanded}
              onToggle={onToggle}
              selectedPath={selectedPath}
              onSelect={onSelect}
              searchTerm={searchTerm}
              showLineNumbers={showLineNumbers}
              lineNumber={lineNumber}
              renderValue={renderValue}
              density={density}
              autoDetectBase64={autoDetectBase64}
            />
          ))}
          <div style={{ ...styles.row, paddingLeft: `${indent}px`, padding: rowPadding }}>
            {showLineNumbers && <span style={styles.lineNumber}>{lineNumber.current++}</span>}
            <span style={styles.bracket}>{bracketClose}</span>
          </div>
        </div>
      )}
    </div>
  )
}

// ── Base64 String Node ───────────────────────────────────────────────────────

interface Base64StringNodeProps extends Omit<JsonNodeProps, 'value'> {
  value: string
  analysis: Base64Analysis
}

/**
 * Renders a base64-encoded string with an inline expand/collapse affordance.
 * Defaults to expanded so the operator sees the decoded content immediately;
 * collapsing returns to the raw encoded view.
 */
function Base64StringNode({
  keyName,
  value,
  analysis,
  path,
  depth,
  expanded,
  onToggle,
  selectedPath,
  onSelect,
  searchTerm,
  showLineNumbers,
  lineNumber,
  renderValue,
  density,
  autoDetectBase64,
}: Base64StringNodeProps): ReactNode {
  const [isOpen, setIsOpen] = useState(true)

  const pathKey = path.join('.')
  const isSelected = selectedPath === pathKey
  const currentLine = lineNumber.current++

  const matchesSearch = !!searchTerm && (
    (keyName && keyName.toLowerCase().includes(searchTerm.toLowerCase())) ||
    value.toLowerCase().includes(searchTerm.toLowerCase()) ||
    (analysis.decoded?.toLowerCase().includes(searchTerm.toLowerCase()) ?? false)
  )

  const indent = depth * 16
  const rowPadding = density === 'compact' ? '2px 0' : '4px 0'

  const rowStyle: CSSProperties = {
    ...styles.row,
    padding: rowPadding,
    paddingLeft: `${indent}px`,
    background: isSelected ? 'var(--bg-hover)' : matchesSearch ? 'rgba(59, 130, 246, 0.1)' : undefined,
  }

  const handleClick = () => {
    onSelect(pathKey)
  }

  const handleToggle = (e: React.MouseEvent) => {
    e.stopPropagation()
    setIsOpen((v) => !v)
  }

  const handleKeyDown = (e: KeyboardEvent) => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault()
      handleClick()
    } else if (e.key === 'ArrowRight' && !isOpen) {
      e.preventDefault()
      setIsOpen(true)
    } else if (e.key === 'ArrowLeft' && isOpen) {
      e.preventDefault()
      setIsOpen(false)
    }
  }

  return (
    <div role="treeitem" aria-expanded={isOpen} aria-selected={isSelected}>
      <div
        style={rowStyle}
        onClick={handleClick}
        onKeyDown={handleKeyDown}
        tabIndex={0}
      >
        {showLineNumbers && <span style={styles.lineNumber}>{currentLine}</span>}
        <button
          type="button"
          onClick={handleToggle}
          style={styles.base64Chevron}
          aria-label={isOpen ? 'Collapse decoded view' : 'Expand decoded view'}
        >
          {isOpen ? <ChevronDown size={12} /> : <ChevronRight size={12} />}
        </button>
        {keyName !== null && (
          <>
            <span style={styles.key}>{keyName}</span>
            <span style={styles.colon}>: </span>
          </>
        )}
        <span style={getValueStyle('string')}>{formatValue(value)}</span>
        <span style={styles.base64Badge} title="Base64-encoded value">base64</span>
      </div>

      {isOpen && (
        <div role="group" style={{ paddingLeft: `${indent + 16}px`, paddingTop: '2px' }}>
          <div style={styles.base64DecodedHeader}>Decoded</div>
          {analysis.json !== undefined ? (
            // Recursively render the decoded JSON tree. Re-using JsonNode means
            // base64 detection works at any depth — e.g. a Pub/Sub envelope
            // whose inner `message.data` is itself base64.
            <div style={styles.base64DecodedBlock}>
              <JsonNode
                keyName={null}
                value={analysis.json}
                path={[...path, '__decoded__']}
                depth={0}
                expanded={expanded}
                onToggle={onToggle}
                selectedPath={selectedPath}
                onSelect={onSelect}
                searchTerm={searchTerm}
                showLineNumbers={false}
                lineNumber={lineNumber}
                renderValue={renderValue}
                density={density}
                autoDetectBase64={autoDetectBase64}
              />
            </div>
          ) : (
            <pre style={styles.base64DecodedText}>{analysis.decoded}</pre>
          )}
        </div>
      )}
    </div>
  )
}

function formatValue(value: unknown): string {
  if (value === null) return 'null'
  if (typeof value === 'string') {
    // Truncate very long strings to prevent performance issues
    const maxLen = 1000
    if (value.length > maxLen) {
      return `"${value.slice(0, maxLen)}..." (${value.length} chars)`
    }
    return `"${value}"`
  }
  if (typeof value === 'boolean') return String(value)
  if (typeof value === 'number') return String(value)
  return String(value)
}

function getValueStyle(type: string): CSSProperties {
  switch (type) {
    case 'string':
      return { color: 'var(--json-string-color)' }
    case 'number':
      return { color: 'var(--json-number-color)' }
    case 'boolean':
      return { color: 'var(--purple)' }
    case 'null':
      return { color: 'var(--json-null-color)', fontStyle: 'italic' }
    default:
      return { color: 'var(--json-text)' }
  }
}

// ── Component ────────────────────────────────────────────────────────────────

/**
 * JsonInspector renders a collapsible, syntax-highlighted JSON tree.
 *
 * Features:
 * - Syntax highlighting for all JSON types
 * - Expand/collapse nodes with keyboard support (j/k, arrows, Enter)
 * - Copy as JSON / copy path
 * - Search-within-tree with highlight + jump
 * - Path display on selection
 * - Three density variants
 * - ARIA tree pattern for accessibility
 */
export function JsonInspector({
  data,
  density = 'compact',
  defaultExpand = 'first-level',
  showCopy = true,
  showSearch = true,
  showLineNumbers = false,
  autoDetectBase64 = true,
  renderValue,
  trailingHeader,
}: JsonInspectorProps): ReactNode {
  const [expanded, setExpanded] = useState<Set<string>>(() =>
    getInitialExpanded(data, defaultExpand)
  )
  const [selectedPath, setSelectedPath] = useState<string | null>(null)
  const [searchTerm, setSearchTerm] = useState('')
  const [copied, setCopied] = useState(false)

  const handleToggle = useCallback((path: string) => {
    setExpanded((prev) => {
      const next = new Set(prev)
      if (next.has(path)) {
        next.delete(path)
      } else {
        next.add(path)
      }
      return next
    })
  }, [])

  const handleCopy = useCallback(async () => {
    try {
      await navigator.clipboard.writeText(JSON.stringify(data, null, 2))
      setCopied(true)
      setTimeout(() => setCopied(false), 2000)
    } catch (err) {
      console.error('Failed to copy:', err)
    }
  }, [data])

  const handleExpandAll = useCallback(() => {
    setExpanded(getInitialExpanded(data, 'all'))
  }, [data])

  const handleCollapseAll = useCallback(() => {
    setExpanded(new Set())
  }, [])

  const lineNumber = useMemo(() => ({ current: 1 }), [data])

  return (
    <div style={styles.container}>
      {/* Header */}
      <div style={styles.header}>
        <div style={styles.headerActions}>
          {showSearch && (
            <div style={styles.searchWrapper}>
              <Search size={12} style={styles.searchIcon} />
              <input
                type="text"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                placeholder="Search..."
                style={styles.searchInput}
                className="input"
                aria-label="Search JSON"
              />
            </div>
          )}

          <button
            type="button"
            onClick={handleExpandAll}
            style={styles.headerButton}
            aria-label="Expand all"
          >
            Expand all
          </button>
          <button
            type="button"
            onClick={handleCollapseAll}
            style={styles.headerButton}
            aria-label="Collapse all"
          >
            Collapse all
          </button>

          {showCopy && (
            <button
              type="button"
              onClick={handleCopy}
              style={styles.headerButton}
              aria-label="Copy as JSON"
            >
              {copied ? <Check size={12} /> : <Copy size={12} />}
              {copied ? 'Copied' : 'Copy'}
            </button>
          )}
        </div>

        {trailingHeader}
      </div>

      {/* Path display */}
      {selectedPath && (
        <div style={styles.pathDisplay}>
          <span style={styles.pathLabel}>Path:</span>
          <code style={styles.pathValue}>{selectedPath || '(root)'}</code>
        </div>
      )}

      {/* Tree */}
      <div
        style={styles.tree}
        role="tree"
        aria-label="JSON tree"
      >
        <JsonNode
          keyName={null}
          value={data}
          path={[]}
          depth={0}
          expanded={expanded}
          onToggle={handleToggle}
          selectedPath={selectedPath}
          onSelect={setSelectedPath}
          searchTerm={searchTerm}
          showLineNumbers={showLineNumbers}
          lineNumber={lineNumber}
          renderValue={renderValue}
          density={density}
          autoDetectBase64={autoDetectBase64}
        />
      </div>
    </div>
  )
}

// ── Styles ───────────────────────────────────────────────────────────────────

const styles: Record<string, CSSProperties> = {
  container: {
    display: 'flex',
    flexDirection: 'column',
    background: 'var(--json-bg)',
    borderRadius: 'var(--radius-md)',
    border: '1px solid var(--border-subtle)',
    overflow: 'hidden',
  },
  header: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: '8px',
    padding: '8px 12px',
    borderBottom: '1px solid var(--border-subtle)',
    background: 'var(--bg-secondary)',
  },
  headerActions: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
  },
  headerButton: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: '4px',
    padding: '4px 8px',
    fontSize: '11px',
    fontWeight: 500,
    color: 'var(--text-muted)',
    background: 'transparent',
    border: 'none',
    borderRadius: 'var(--radius-sm)',
    cursor: 'pointer',
    transition: 'all var(--transition-fast)',
  },
  searchWrapper: {
    position: 'relative',
    display: 'flex',
    alignItems: 'center',
  },
  searchIcon: {
    position: 'absolute',
    left: '8px',
    color: 'var(--text-muted)',
    pointerEvents: 'none',
  },
  searchInput: {
    width: '140px',
    height: '24px',
    paddingLeft: '26px',
    paddingRight: '8px',
    fontSize: '11px',
    background: 'var(--bg-tertiary)',
    border: '1px solid var(--border-subtle)',
    borderRadius: 'var(--radius-sm)',
    color: 'var(--text-primary)',
    outline: 'none',
  },
  pathDisplay: {
    display: 'flex',
    alignItems: 'center',
    gap: '6px',
    padding: '6px 12px',
    borderBottom: '1px solid var(--border-subtle)',
    background: 'var(--bg-secondary)',
    fontSize: '11px',
  },
  pathLabel: {
    color: 'var(--text-muted)',
  },
  pathValue: {
    fontFamily: 'var(--json-font-family)',
    fontSize: 'var(--json-font-size)',
    color: 'var(--json-key-color)',
  },
  tree: {
    padding: '8px 0',
    fontFamily: 'var(--json-font-family)',
    fontSize: 'var(--json-font-size)',
    lineHeight: 'var(--json-line-height)',
    overflowX: 'auto',
    overflowY: 'auto',
    maxHeight: '400px',
  },
  row: {
    display: 'flex',
    alignItems: 'center',
    gap: '2px',
    paddingRight: '12px',
    cursor: 'pointer',
    transition: 'background var(--transition-fast)',
    outline: 'none',
    minHeight: '20px',
  },
  lineNumber: {
    width: '32px',
    paddingRight: '8px',
    textAlign: 'right',
    color: 'var(--text-faint)',
    fontSize: '10px',
    userSelect: 'none',
    flexShrink: 0,
  },
  expandIcon: {
    width: '16px',
    height: '16px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    color: 'var(--text-muted)',
    flexShrink: 0,
  },
  key: {
    color: 'var(--json-key-color)',
  },
  colon: {
    color: 'var(--text-muted)',
  },
  bracket: {
    color: 'var(--text-muted)',
  },
  summary: {
    color: 'var(--text-faint)',
    fontStyle: 'italic',
    marginLeft: '4px',
  },
  base64Chevron: {
    width: '16px',
    height: '16px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    padding: 0,
    background: 'transparent',
    border: 'none',
    color: 'var(--text-muted)',
    cursor: 'pointer',
    flexShrink: 0,
  },
  base64Badge: {
    marginLeft: '8px',
    padding: '1px 6px',
    fontSize: '10px',
    fontWeight: 600,
    textTransform: 'uppercase',
    letterSpacing: '0.5px',
    color: 'var(--accent-secondary)',
    background: 'rgba(96, 165, 250, 0.12)',
    border: '1px solid rgba(96, 165, 250, 0.3)',
    borderRadius: 'var(--radius-sm)',
    fontFamily: 'var(--font-sans, system-ui)',
    flexShrink: 0,
  },
  base64DecodedHeader: {
    fontSize: '10px',
    fontWeight: 600,
    textTransform: 'uppercase',
    letterSpacing: '0.5px',
    color: 'var(--text-faint)',
    marginBottom: '4px',
    fontFamily: 'var(--font-sans, system-ui)',
  },
  base64DecodedBlock: {
    borderLeft: '2px solid rgba(96, 165, 250, 0.3)',
    paddingLeft: '8px',
    marginBottom: '4px',
  },
  base64DecodedText: {
    margin: 0,
    padding: '8px',
    borderLeft: '2px solid rgba(96, 165, 250, 0.3)',
    background: 'var(--bg-primary)',
    color: 'var(--text-secondary)',
    fontFamily: 'var(--json-font-family)',
    fontSize: 'var(--json-font-size)',
    lineHeight: 'var(--json-line-height)',
    whiteSpace: 'pre-wrap',
    wordBreak: 'break-word',
    maxHeight: '300px',
    overflowY: 'auto',
    marginBottom: '4px',
  },
}

export default JsonInspector
